import { isEqual } from 'lodash';

/**
 * Determines if any fields have changed between the initial and current block configurations.
 * Compares fields by name and uses deep equality to detect changes in field values.
 * Returns true if any field from the initial block is different in the current block,
 * including cases where fields are missing in the current block.
 *
 * @param {Object} currentBlock - The current block object containing a fields array
 * @param {Object} initialBlock - The initial block object containing a fields array to compare against
 * @returns {boolean} Returns true if any field has changed, false if all fields are identical
 *
 * @example
 * // Field value changed
 * const initial = {
 *   fields: [{ name: 'email', value: 'old@example.com', type: 'input' }]
 * };
 * const current = {
 *   fields: [{ name: 'email', value: 'new@example.com', type: 'input' }]
 * };
 * areFieldsChanged(current, initial); // true
 *
 * @example
 * // No changes detected
 * const block = {
 *   fields: [{ name: 'email', value: 'test@example.com', type: 'input' }]
 * };
 * areFieldsChanged(block, block); // false
 *
 * @example
 * // Field missing in current block
 * const initial = { fields: [{ name: 'email', value: 'test@example.com' }] };
 * const current = { fields: [] };
 * areFieldsChanged(current, initial); // true
 *
 * @example
 * // Field added in current block (not detected as change)
 * const initial = { fields: [{ name: 'email', value: 'test@example.com' }] };
 * const current = {
 *   fields: [
 *     { name: 'email', value: 'test@example.com' },
 *     { name: 'phone', value: '123-456-7890' }
 *   ]
 * };
 * areFieldsChanged(current, initial); // false
 *
 * @example
 * // Complex field objects
 * const initial = {
 *   fields: [{
 *     name: 'config',
 *     value: { timeout: 1000, retries: 3 },
 *     validation: { required: true }
 *   }]
 * };
 * const current = {
 *   fields: [{
 *     name: 'config',
 *     value: { timeout: 2000, retries: 3 },
 *     validation: { required: true }
 *   }]
 * };
 * areFieldsChanged(current, initial); // true
 */
export const areFieldsChanged = (currentBlock, initialBlock) =>
    initialBlock.fields.some((initialField) => {
        const currentField = currentBlock.fields.find(
            (currentBlockField) => currentBlockField?.name === initialField?.name,
        );

        return !isEqual(initialField, currentField);
    });
